/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setToken, clearToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [household, setHousehold] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadHousehold = useCallback(async () => {
    try {
      const { data } = await api.get("/household");
      setHousehold(data.household);
      setMembers(data.members || []);
    } catch (err) {
      // Household may not exist yet (e.g. just-registered mediator) — soft-fail and log.
      console.warn("loadHousehold failed:", err?.response?.status || err?.message);
    }
  }, []);

  const refresh = useCallback(async () => {
    // v77.99.103 — Authoritative session check via the cookie-driven
    // /auth/me endpoint. The legacy getToken() short-circuit is gone:
    // it returned "" since v77.99.102, which meant any explicit
    // refresh() call wiped the user state and effectively logged
    // people out (surfaced by the mobile-nav star toggle + Settings
    // → Customise Navigation save). The browser sends the httpOnly
    // cookie automatically; we trust the backend to 401 if there
    // really is no session.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadHousehold();
    } catch (err) {
      const status = err?.response?.status;
      console.warn("auth refresh failed:", status || err?.message);
      if (status === 401 || status === 403) {
        // Real auth failure — clear local state and let
        // ProtectedRoute redirect to /auth.
        clearToken();
        setUser(null);
        setHousehold(null);
        setMembers([]);
      }
      // Network / 5xx errors: keep the existing user state. A flaky
      // network must NOT silently sign the user out.
    } finally {
      setLoading(false);
    }
  }, [loadHousehold]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password, totp_code, extras = {}) => {
    const { data } = await api.post("/auth/login", {
      email, password, totp_code: totp_code || null,
      ...extras,
    });
    // v77.18 — Archived account: surface the restore path. The caller
    // is expected to render the "Your account was archived" screen
    // when this branch fires. We deliberately DON'T set the regular
    // user state here — there's no full session yet.
    if (data.account_archived) {
      return {
        archived: true,
        can_restore: !!data.can_restore,
        days_remaining: data.days_remaining || 0,
        archived_at: data.archived_at,
        restore_token: data.restore_token,
        email: data.email,
      };
    }
    setToken(data.token);
    setUser(data.user);
    await loadHousehold();
    return { user: data.user, welcome_back: !!data.welcome_back };
  }, [loadHousehold]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token);
    setUser(data.user);
    await loadHousehold();
    return data.user;
  }, [loadHousehold]);

  const logout = useCallback(async () => {
    // v77.99.4 — Server-side logout: revokes the session row + clears
    // the httpOnly cookie. Best-effort: if the network call fails
    // (offline, 401), we still wipe local state so the UI never
    // becomes stuck signed-in client-side.
    try { await api.post("/auth/logout"); } catch (e) { console.debug("[auth] logout call failed:", e?.message); }
    clearToken();
    // v77.99 — also drop any impersonation backup. A normal logout
    // ends both layers cleanly.
    try { localStorage.removeItem("cpc_admin_token_backup"); } catch (e) { console.debug("[auth] localStorage cleanup failed:", e?.message); }
    setUser(null); setHousehold(null); setMembers([]);
  }, []);

  // v77.99 — Admin impersonation. `startImpersonation` calls the
  // backend, swaps the active token to the impersonation JWT, and
  // squirrels the original admin token in a separate localStorage
  // slot (`cpc_admin_token_backup`) for restore. `stopImpersonation`
  // pings the backend audit endpoint then puts the admin token back.
  // Both methods refresh the user state so the banner / dashboard
  // re-renders immediately.
  const startImpersonation = useCallback(async ({ user_id, reason }) => {
    const adminToken = getToken();
    const { data } = await api.post("/admin/impersonate/start", { user_id, reason });
    try { localStorage.setItem("cpc_admin_token_backup", adminToken || ""); } catch (e) { console.debug("[auth] backup token write failed:", e?.message); }
    setToken(data.token);
    await refresh();
    return data;
  }, [refresh]);
  const stopImpersonation = useCallback(async () => {
    try { await api.post("/admin/impersonate/stop"); }
    catch (e) { console.debug("[auth] stop impersonation call failed:", e?.message); }
    let backup = "";
    try { backup = localStorage.getItem("cpc_admin_token_backup") || ""; } catch (e) { console.debug("[auth] backup token read failed:", e?.message); }
    if (backup) {
      setToken(backup);
      try { localStorage.removeItem("cpc_admin_token_backup"); } catch (e) { console.debug("[auth] backup token cleanup failed:", e?.message); }
      await refresh();
    } else {
      // No backup — admin lost the original token (browser cleared,
      // device swapped). Treat as logout; admin will need to sign in
      // again to resume admin work.
      logout();
    }
  }, [refresh, logout]);

  const currency = household?.currency || "EUR";
  const isMediator = user?.role === "mediator";
  const mediatorPerms = isMediator ? (user?.permissions || {}) : null;
  const canView = useCallback(
    (key) => !isMediator || mediatorPerms?.[key] !== false,
    [isMediator, mediatorPerms],
  );

  const value = useMemo(
    () => ({ user, setUser, household, members, currency, isMediator, mediatorPerms, canView, loading, login, register, logout, refresh, loadHousehold, startImpersonation, stopImpersonation }),
    [user, household, members, currency, isMediator, mediatorPerms, canView, loading, login, register, logout, refresh, loadHousehold, startImpersonation, stopImpersonation],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
